/**
 * Crisingmodal Component
 * Scrollable modal variant that appears offset from the top with gradient border
 */

'use client';

import { useCallback, useEffect, useId, useRef, type CSSProperties, type ReactNode } from 'react';
import { usePersistentDebugLogger } from '@/hooks/telemetry/use-persistent-debug-logger';
import type { DataElScope } from '@/utils/dataEl';
import { ContainerPreset } from '@/constants/componentPresets';
import { ColorType } from '@/theme/colors';
import { useViewport } from '@/hooks/dom/use-viewport';
import { Viewport } from '@/theme/breakpoints';
import { CrisingmodalView } from './CrisingmodalView';
import {
  DEFAULT_BOX_SHADOW,
  DEFAULT_MAX_WIDTH,
  DEFAULT_TOP_RATIO,
} from './crisingmodalConstants';
import {
  buildContainerModel,
  buildCrisingmodalStyleModel,
  buildRisingBackdrop,
  getCardWidth,
} from './crisingmodalModel';
import { useCrisingmodalRegistration } from './useCrisingmodalRegistration';
import {
  CRISINGMODAL_DISMISS_DEBUG_COMPONENT,
  CRISINGMODAL_DISMISS_DEBUG_INSTANCE_ID,
  CrisingmodalDismissDebugBranch,
  CrisingmodalDismissDebugEvent,
} from './crisingmodalDismissDebug';

const ESCAPE_KEY = 'Escape';

export enum CrisingmodalPreset {
  Default = 'default',
  Invisimodal = 'invisimodal',
}

export interface CrisingmodalProps {
  /** The component rendering this modal, e.g. "Cpolicymodal/Modal". Required: it is
   *  what makes an inspected node say WHICH modal it belongs to. */
  dataElScope: DataElScope;
  visible: boolean;
  children?: ReactNode;
  modalPreset?: CrisingmodalPreset;
  onClose?: () => void;
  /**
   * Optional callback for the back button.
   * Defaults to onClose when not provided.
   */
  onBack?: () => void;
  showBackButton?: boolean;
  maxWidth?: number;
  topOffsetRatio?: number;
  /**
   * Optional specific top offset value (e.g., "50px", "2rem").
   * If provided, overrides topOffsetRatio.
   */
  topOffset?: string | number;
  padding?: number;
  backdropBlur?: number;
  backdropOpacity?: number;
  backdropUseGradient?: boolean;
  containerPreset?: ContainerPreset;
  containerBackgroundColor?: ColorType;
  containerBackgroundOpacity?: number;
  containerBackdropBlur?: number;
  containerRadius?: number;
  boxShadow?: string;
  containerStyle?: CSSProperties;
  contentWrapperStyle?: CSSProperties;
  contentStyle?: CSSProperties;
  scrollableStyle?: CSSProperties;
  /**
   * Optional inset applied to the back button from the top-right corner.
   * Defaults to 1rem.
   */
  backButtonInset?: string | number;
  scrollRef?: React.RefObject<HTMLDivElement | null>;
  fromBottom?: boolean;
  /**
   * Optional top margin applied to the back-button + card stack when rising from the bottom.
   */
  fromBottomStackMarginTop?: string | number;
  /**
   * Optional top margin applied to the internal `Ccontainer` root when rising from the bottom.
   */
  fromBottomContainerMarginTop?: string | number;
  /**
   * Modal nesting index (1 = first modal, 2 = modal within modal, etc.)
   * Each increment adds 4rem to the top offset. Stacking z-index is derived
   * from the shared modal stack, not from this value.
   */
  modalIndex?: number;
}

export function Crisingmodal({
  dataElScope,
  visible,
  children,
  modalPreset = CrisingmodalPreset.Default,
  onClose,
  onBack,
  showBackButton = true,
  maxWidth = DEFAULT_MAX_WIDTH,
  topOffsetRatio = DEFAULT_TOP_RATIO,
  topOffset: customTopOffset,
  padding = 32,
  backdropBlur,
  backdropOpacity,
  backdropUseGradient,
  containerPreset = ContainerPreset.Default,
  containerBackgroundColor = ColorType.Black,
  containerBackgroundOpacity = 0.75,
  containerBackdropBlur,
  containerRadius = 72,
  boxShadow = DEFAULT_BOX_SHADOW,
  containerStyle,
  contentWrapperStyle,
  contentStyle,
  scrollableStyle: scrollableStyleOverride,
  backButtonInset = '1rem',
  scrollRef,
  fromBottom = true,
  fromBottomStackMarginTop,
  fromBottomContainerMarginTop,
  modalIndex = 1,
}: CrisingmodalProps): ReactNode {
  const { viewport } = useViewport();
  const isMobile = viewport === Viewport.Mobile;
  const isInvisimodal = modalPreset === CrisingmodalPreset.Invisimodal;

  void maxWidth;
  void topOffsetRatio;
  void customTopOffset;
  void fromBottom;
  void modalIndex;

  const instanceUuid = useId();
  const dismissDebug = usePersistentDebugLogger({
    component: CRISINGMODAL_DISMISS_DEBUG_COMPONENT,
    instanceId: CRISINGMODAL_DISMISS_DEBUG_INSTANCE_ID,
    metadata: { instanceUuid },
  });
  const previousVisibleRef = useRef(visible);

  useEffect(() => {
    dismissDebug.logMarker({
      name: CrisingmodalDismissDebugEvent.Mounted,
      payload: { instanceUuid, visibleAtMount: visible, pathname: window.location.pathname },
    });
    return () => {
      dismissDebug.logMarker({
        name: CrisingmodalDismissDebugEvent.Unmounted,
        payload: { instanceUuid },
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount/unmount marker only
  }, []);

  useEffect(() => {
    if (previousVisibleRef.current === visible) return;
    dismissDebug.logStateTransition({
      name: CrisingmodalDismissDebugEvent.VisibleChanged,
      from: previousVisibleRef.current,
      to: visible,
      payload: { instanceUuid },
    });
    previousVisibleRef.current = visible;
  }, [visible, dismissDebug, instanceUuid]);

  const handleClose = useCallback((): void => {
    dismissDebug.logEventFlow({
      name: CrisingmodalDismissDebugEvent.HandleClose,
      payload: { instanceUuid, hasOnClose: Boolean(onClose), visible },
    });
    onClose?.();
  }, [dismissDebug, instanceUuid, onClose, visible]);

  const handleBack = useCallback((): void => {
    dismissDebug.logBranchChoice({
      name: CrisingmodalDismissDebugEvent.HandleBack,
      selectedBranch: onBack
        ? CrisingmodalDismissDebugBranch.OnBack
        : CrisingmodalDismissDebugBranch.FallbackClose,
      payload: { instanceUuid, visible },
    });
    if (onBack) {
      onBack();
      return;
    }
    handleClose();
  }, [dismissDebug, handleClose, instanceUuid, onBack, visible]);

  const risingBackdrop = buildRisingBackdrop({
    backdropBlur,
    backdropOpacity,
    backdropUseGradient,
    isInvisimodal,
  });
  const registration = useCrisingmodalRegistration({
    handleBack,
    risingBackdrop,
    visible,
  });
  const isTopOpen = registration.isTopOpen;
  useEffect(() => {
    if (!visible || !isTopOpen) return;

    const handleEscapeKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== ESCAPE_KEY) return;
      event.preventDefault();
      handleBack();
    };
    window.addEventListener('keydown', handleEscapeKeyDown);
    return () => window.removeEventListener('keydown', handleEscapeKeyDown);
  }, [handleBack, isTopOpen, visible]);
  const containerModel = buildContainerModel({
    boxShadow,
    containerBackdropBlur,
    containerBackgroundColor,
    containerBackgroundOpacity,
    containerMarginTop: fromBottomContainerMarginTop,
    containerPreset,
    containerStyle,
    isInvisimodal,
  });
  const handleExitComplete = (): void => {
    dismissDebug.logEventFlow({
      name: CrisingmodalDismissDebugEvent.ExitComplete,
      payload: { instanceUuid, visible },
    });
    registration.completeExit();
  };
  const styleModel = buildCrisingmodalStyleModel({
    cardWidth: getCardWidth({ isMobile }),
    contentStyle,
    contentWrapperStyle,
    isInvisimodal,
    registrationContentZIndex: registration.contentZIndex,
    scrollableStyleOverride,
    stackMarginTop: fromBottomStackMarginTop,
  });

  return (
    <CrisingmodalView
      backButtonInset={backButtonInset}
      containerModel={containerModel}
      containerRadius={containerRadius}
      dataElScope={dataElScope}
      handleBack={handleBack}
      handleClose={handleClose}
      onExitComplete={handleExitComplete}
      padding={padding}
      scrollRef={scrollRef}
      showBackButton={showBackButton}
      styleModel={styleModel}
      visible={visible}
    >
      {children}
    </CrisingmodalView>
  );
}
